import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import accounting from 'accounting';
import Badge from 'components/v2/Badge';
import SessionStore from 'stores/SessionStore';
import AddButton from 'components/v2/buttons/AddButton';
import DeleteButton from 'components/v2/buttons/DeleteButton';
import {
  getAccessorials,
  getStandardAccessorials,
  upsertStandardAccessorials,
} from 'utils/AccessorialWebAPIUtils';
import ToastStore from 'stores/ToastStore';
import TextField from 'components/v2/forms/TextField';
import CargomaticSelect from 'components/v2/select';
import EditButton from 'components/v2/buttons/EditButton';
import LoadingCircle from 'components/ui/LoadingCircle';
import { titleize } from 'underscore.string';

export default function StandardAccessorialRates({
  canUpdate,
  type,
  markets,
  onHide,
}) {
  const [standardAccessorialRates, setStandardAccessorialRates] = useState([]);
  const [accessorials, setAccessorials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isButtonDisabled, setIsButtonDisabled] = useState(true);
  const [isValid, setIsValid] = useState(true);

  const { t } = useTranslation();

  useEffect(() => {
    fetchAccessorials();
    fetchStandardAccessorialRates();
  }, []);

  const fetchAccessorials = async () => {
    try {
      const result = await getAccessorials({ paginate: false });
      const {
        entity: { items },
      } = result;

      const list = items.map((a) => ({
        name: a.name,
        code: a.charge_code,
      }));

      console.log('Accessorials', list);
      setAccessorials(list);
    } catch (e) {
      ToastStore.addNotification({
        title: t('LOADING_ACCESSORIALS_ERROR'),
        body: '',
        level: 'error',
      });
    }
  };

  const fetchStandardAccessorialRates = async () => {
    try {
      const filterString = { isActive: true, paginate: false };

      if (markets) {
        filterString.market = markets;
        filterString.customerType = type;
      }
      const query = new URLSearchParams(filterString).toString();
      const result = await getStandardAccessorials(query);
      const {
        entity: {
          results: { standardAccessorialRates },
        },
      } = result;
      if (standardAccessorialRates && standardAccessorialRates.length) {
        setStandardAccessorialRates(standardAccessorialRates);
      }
      setLoading(false);
      setIsButtonDisabled(false);
    } catch (e) {
      console.log(e);
      ToastStore.addNotification({
        title: titleize(t('ERROR')),
        body: t('FAILED_TO_LOAD_ACCESSORIALS'),
        level: 'error',
      });
    }
  };

  function renderHeader(title) {
    return <div data-testid="div-193eb5c9" className="flex whitespace-nowrap">{t(title)}</div>;
  }

  const handleValueChange = (value, type, index) => {
    setIsValid(true);
    const newAccessorialRates = [...standardAccessorialRates];
    const targetStdAccessorialRate = {
      ...standardAccessorialRates[index],
      [type]: value.name || value,
    };
    newAccessorialRates[index] = targetStdAccessorialRate;
    setStandardAccessorialRates(newAccessorialRates);
  };

  const handleAccessorialChange = (value, index) => {
    setIsValid(true);
    const { name } = value;
    const acc = accessorials.find((acc) => acc.name === name);
    const targetAccessorial = {
      ...standardAccessorialRates[index],
      accessorialType: name,
      accessorialCode: acc.code || '',
    };
    const newItems = [...standardAccessorialRates];
    newItems[index] = targetAccessorial;

    setStandardAccessorialRates(newItems);
  };

  const handleEditButtonClick = (item, index) => {
    const targetStdAccessorialRate = {
      ...item,
      editing: !item.editing,
      update: true,
    };
    const newItems = [...standardAccessorialRates];
    newItems[index] = targetStdAccessorialRate;
    setStandardAccessorialRates(newItems);
  };

  const removeStandardAccessorialRateRow = (index) => {
    setStandardAccessorialRates((prev) => {
      return [...prev.slice(0, index), ...prev.slice(index + 1)];
    });
  };

  const handleDeleteButtonClick = async (standardAccessorialRate, index) => {
    setIsValid(true);
    const standardAccessorialRateToDelete = { ...standardAccessorialRate };
    if (
      standardAccessorialRateToDelete.createdAt ||
      standardAccessorialRateToDelete.id
    ) {
      standardAccessorialRateToDelete.isActive = false;
      setIsButtonDisabled(true);
      const result = await handleUpsertStandardAccessorialRates([
        standardAccessorialRateToDelete,
      ]);
      setIsButtonDisabled(false);
      if (!result) {
        return;
      }
    }
    removeStandardAccessorialRateRow(index);
  };

  function addRow() {
    const newItem = {
      accessorialType: '',
      accessorialCode: '',
      rateType: '',
      rate: 0,
      unit: '',
      market: markets[0],
      editing: true,
      update: true,
      isActive: true,
    };
    const newItems = [...standardAccessorialRates, newItem];
    setStandardAccessorialRates(newItems);
  }

  const handleSave = async () => {
    const valid = validate(standardAccessorialRates);

    if (!valid) {
      return setIsValid(false);
    }
    setIsValid(true);

    const accessorialRates = [];
    standardAccessorialRates.forEach((item) => {
      if (item.update) {
        const entry = {
          accessorialType: item.accessorialType,
          accessorialCode: item.accessorialCode,
          rateType: item.rateType,
          rate: item.rate,
          unit: item.unit,
          customerType: type,
          market: item.market,
          isActive: item.isActive,
        };

        if (item.id) {
          entry.id = item.id;
        }

        accessorialRates.push(entry);
      }
    });
    setLoading(true);
    setIsButtonDisabled(true);
    const result = accessorialRates.length
      ? await handleUpsertStandardAccessorialRates(accessorialRates)
      : true;
    setLoading(false);
    setIsButtonDisabled(false);
    if (result) {
      onHide();
    }
  };

  async function handleUpsertStandardAccessorialRates(accessorialRates) {
    try {
      const result = await upsertStandardAccessorials(accessorialRates);
      if (result) {
        const {
          entity: {
            results: {
              result: { total },
            },
          },
        } = result;
        if (total && total.flatten().length) {
          ToastStore.addNotification({
            title: t('STANDARD_ACCESSORIAL_RATES'),
            body: t('SUCCESSFULLY_UPDATED'),
            level: 'success',
          });
        }
      }
      return result;
    } catch {
      ToastStore.addNotification({
        title: t('STANDARD_ACCESSORIAL_RATES'),
        body: 'Unable to update or delete standard accessorial rate',
        level: 'error',
      });
      return false;
    }
  }

  function validate() {
    if (!standardAccessorialRates || !standardAccessorialRates.length) {
      return false;
    }
    for (let i = 0; i < standardAccessorialRates.length; i++) {
      const item = standardAccessorialRates[i];
      if (
        !(
          item.accessorialType &&
          item.accessorialCode &&
          item.rateType &&
          item.rate >= 0 &&
          item.unit &&
          item.market
        )
      ) {
        return false;
      }
    }
    return true;
  }

  /** RENDER FUNCTIONS */

  function renderEditButton(item, i) {
    if (!canUpdate) return <div data-testid="div-630df5e4" />;
    return (
      <EditButton
        dataTestId="EditButton-c619f889"
        disabled={
          !SessionStore.isAbleToEditAccessorialLaneRate() || isButtonDisabled
        }
        onClick={() => handleEditButtonClick(item, i)}
        size="small" />
    );
  }

  function renderDeleteButton(item, i) {
    if (!canUpdate) return <div data-testid="div-79d6ad56" />;
    return (
      <DeleteButton
        dataTestId="DeleteButton-9814ab78"
        disabled={
          !SessionStore.isAbleToEditAccessorialLaneRate() || isButtonDisabled
        }
        onClick={() => handleDeleteButtonClick(item, i)} />
    );
  }

  // render rate input
  const renderRateInput = (rate, i) => {
    return (
      <div data-testid="div-365ed30e" className="w-20">
        <TextField
          dataTestId="TextField-0c757f17"
          type="number"
          value={rate}
          placeholder={t('ENTER_RATE')}
          onChange={(value) => handleValueChange(value, 'rate', i)} />
      </div>
    );
  };

  // render unit dropdown
  const renderUnitDropdown = (unit, i) => {
    return (
      <CargomaticSelect
        dataTestId="CargomaticSelect-b568a888"
        items={[
          { name: 'per day', localizationSlug: 'PER_DAY' },
          { name: 'per event', localizationSlug: 'PER_EVENT' },
        ]}
        onSelect={(value) => handleValueChange(value, 'unit', i)}
        caption={t('UNIT', { count: 1 })}
        selected={unit}
        textKey="localizationSlug"
        valueKey="name" />
    );
  };

  // render percentage dropdwon
  const renderRateTypeDropdown = (rateType, i) => {
    return (
      <CargomaticSelect
        dataTestId="CargomaticSelect-d6174d1f"
        onSelect={(value) => handleValueChange(value, 'rateType', i)}
        title={t('MY_FILTERS')}
        items={['$', '%']}
        selected={rateType} />
    );
  };

  const renderMarketDropdown = (market, i) => {
    return (
      <CargomaticSelect
        dataTestId="CargomaticSelect-b4648aec"
        onSelect={(value) => handleValueChange(value, 'market', i)}
        title={t('MY_FILTERS')}
        items={markets}
        selected={market} />
    );
  };

  const renderAccessorials = (accessorial, i) => {
    if (!accessorials || !accessorials.length) {
      return null;
    }
    return (
      <CargomaticSelect
        dataTestId="CargomaticSelect-93d29733"
        onSelect={(value) => handleAccessorialChange(value, i)}
        title={t('MY_FILTERS')}
        caption={t('SELECT_ACCESSORIALS')}
        showCaption
        items={accessorials}
        selected={accessorial}
        textKey="name"
        valueKey="name" />
    );
  };

  function renderTableRow(item, i) {
    return (
      <tr
        data-testid={`tr-7b58d651_${i}`}
        key={i}
        className={i === standardAccessorialRates.length - 1 ? 'border-b' : ''}>
        <td data-testid="td-53c650f4">
          {type === 'carrier' ? (
            <Badge dataTestId="Badge-2823f613" color="blue" size="large" rounded>
              {titleize(t('CARRIER'))}
            </Badge>
          ) : (
            <Badge dataTestId="Badge-6f88e466" color="green" size="large" rounded>
              {titleize(t('SHIPPER'))}
            </Badge>
          )}
        </td>
        <td data-testid="td-f15cb1d3">{item.market}</td>
        <td data-testid="td-51765d8e">{item.accessorialType}</td>
        <td data-testid="td-09849ee5">{item.accessorialCode}</td>
        <td data-testid="td-321d96f8">{item.rateType}</td>
        <td data-testid="td-ef505b1e">{accounting.formatMoney(item.rate, '$', 0)}</td>
        <td data-testid="td-d5cf2888">{item.unit}</td>
        <td data-testid="td-c539414e">{renderEditButton(item, i)}</td>
        <td data-testid="td-04cd49e8">{renderDeleteButton(item, i)}</td>
      </tr>
    );
  }

  function renderEditableRow(item, i) {
    return (
      <tr
        data-testid={`tr-9fb36bc5_${i}`}
        key={i}
        className={i === standardAccessorialRates.length - 1 ? 'border-b' : ''}>
        <td data-testid="td-721e54f5">
          {type === 'carrier' ? (
            <Badge dataTestId="Badge-1c45e165" color="blue" size="large" rounded>
              {titleize(t('CARRIER'))}
            </Badge>
          ) : (
            <Badge dataTestId="Badge-45ab21ac" color="green" size="large" rounded>
              {titleize(t('SHIPPER'))}
            </Badge>
          )}
        </td>
        <td data-testid="td-b3c4cebb">{renderMarketDropdown(item.market, i)}</td>
        <td data-testid="td-1034e07f">{renderAccessorials(item.accessorialType, i)}</td>
        <td data-testid="td-1d29b460">{item.accessorialCode}</td>
        <td data-testid="td-a4b9f02e">{renderRateTypeDropdown(item.rateType, i)}</td>
        <td data-testid="td-5d03e4a2">{renderRateInput(item.rate, i)}</td>
        <td data-testid="td-3e53b6f0">{renderUnitDropdown(item.unit, i)}</td>
        <td data-testid="td-9cecd31d">{renderEditButton(item, i)}</td>
        <td data-testid="td-ef5938db">{renderDeleteButton(item, i)}</td>
      </tr>
    );
  }

  function renderRow(item, i) {
    if (item.editing) {
      return renderEditableRow(item, i);
    }
    if (!item.editing) {
      return renderTableRow(item, i);
    }
  }

  if (loading)
    return (
      <div data-testid="div-c416348f" className="flex justify-center p-14">
        <LoadingCircle dataTestId="LoadingCircle-62bb9b30" text={t('WORKING_DOTS')} />
      </div>
    );

  return (
    <div data-testid="div-42249e1e" className="overflow-x-auto mb-6">
      {canUpdate && (
        <div data-testid="div-23ee61da" className="flex flex-row-reverse mb-4 h-4">
          <AddButton
            dataTestId="AddButton-44545970"
            className="absolute right-3 top-3"
            onClick={addRow} />
        </div>
      )}
      <table data-testid="table-5ee54b02" className="default-table">
        <thead data-testid="thead-db2f30a2">
          <tr data-testid="tr-6065280e">
            <th data-testid="th-e83fd91c">{renderHeader(t('CUSTOMER_TYPE').toUpperCase())}</th>
            <th data-testid="th-bb95db22">
              {renderHeader(titleize(t('MARKET', { count: 1 }).toUpperCase()))}
            </th>
            <th data-testid="th-7886f9c1">{renderHeader(t('ACCESSORIAL_TYPE').toUpperCase())}</th>
            <th data-testid="th-08bcd37a">{renderHeader(t('ACCCESSORIAL_CODE').toUpperCase())}</th>
            <th data-testid="th-00b60566">{renderHeader(t('DOLLAR_PERCENTAGE').toUpperCase())}</th>
            <th data-testid="th-562a2f20">{renderHeader(t('RATE', { count: 1 }).toUpperCase())}</th>
            <th data-testid="th-efed3f0d">{renderHeader(t('UNIT', { count: 1 }).toUpperCase())}</th>
          </tr>
        </thead>
        <tbody data-testid="tbody-c38b5418">
          {standardAccessorialRates && standardAccessorialRates.length ? (
            standardAccessorialRates.map((item, i) => renderRow(item, i))
          ) : (
            <div data-testid="div-771e6e28" className="flex justify-center mt-24">{t('EMPTY_STATE')}</div>
          )}
        </tbody>
      </table>
      <div data-testid="div-feac08e5" className="flex flex-row justify-center">
        {!isValid && (
          <span data-testid="span-50497b90" className="text-red mt-4">
            {t('PLEASE_CHECK_REQUIRED_DATE')}
          </span>
        )}
      </div>
      <div data-testid="div-f433dace" className="flex flex-row-reverse mt-8">
        {canUpdate && (
          <button
            data-testid="button-4ed03f29"
            onClick={handleSave}
            className="button button-green ml-4"
            disabled={isButtonDisabled}>
            {t('SAVE').toUpperCase()}
          </button>
        )}
        <button
          data-testid="button-59372dc2"
          onClick={onHide}
          className="button button-blue"
          disabled={isButtonDisabled}>
          {t('CANCEL').toUpperCase()}
        </button>
      </div>
    </div>
  );
}
